import { Distance, EventEmitter, Logger } from '../../../../FrontendBundle/Resources/js/ofcs';
import CutItemCommand from './command/cutItemCommand';
import PasteItemCommand from './command/pasteItemCommand';

/**
 * The clipboard implements the copy and paste functionalities for items. The
 * clipboard should be a global instance. Items to copy has to be given to
 * the clipboard. If items should be pasted, the instance which should receive
 * the new items has to be given.
 */
export default class Clipboard extends EventEmitter {
    /**
     * Clipboard adapter for storing items
     *
     * @private
     * @property {AdapterInterface} _adapter
     */

    /**
     * Item factory instance to create new items from local storage json
     *
     * @private
     * @property {Ofcs.Editor.Item.ItemFactory} _itemFactory
     */

    /**
     * Selection collection instance to access selected items
     *
     * @private
     * @property {Ofcs.Editor.Canvas.SelectionCollection} _selection
     */

    /**
     * Context manager
     *
     * @private
     * @property {Ofcs.Editor.Context.ContextManager} _contextManager
     */

    /**
     * History instance for inserting commands
     *
     * @private
     * @property {Ofcs.Editor.Command.History} _history
     */

    /**
     * Canvas instance for positioning inserted items
     *
     * @property {Ofcs.Editor.Canvas} _canvas
     */

    /**
     * The change event is triggered when the content of the clipboard will
     * be changed
     *
     * @event change
     * @param {Boolean} hasItems True if clipboard holds items
     */

    /**
     * Creates a new instance of the clipboard
     *
     * @constructor
     * @param {AdapterInterface} adapter
     * @param {ItemFactory} itemFactory
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection
     * @param {ContextManager} contextManager
     * @param {History} history
     * @param {Ofcs.Editor.Canvas} canvas
     * @param {EditorItemFilter} deletableItemsFilter
     */
    constructor(adapter, itemFactory, selection, contextManager, history, canvas, deletableItemsFilter) {
        super();
        this._adapter = adapter;
        this._itemFactory = itemFactory;
        this._selection = selection;
        this._contextManager = contextManager;
        this._history = history;
        this._canvas = canvas;
        this._deletableItemsFilter = deletableItemsFilter;

        adapter.on('change', () => {
            this.trigger('change', adapter.hasItems(this._getCurrentContextClassification()));
        });

        this._contextManager.on('switch', () => {
            this.trigger('change', adapter.hasItems(this._getCurrentContextClassification()));
        });
    }

    _isCopySupportedBySelection() {
        return !this._selection
            .toArray()
            .find((item) => {
                if (!item.supportsCopying) return true;
                if (!item.getParentItem().supportsMultiChildCopy && this._selection.length > 1) return true;

                return false;
            });
    }

    /**
     * Copy selected items to clipboard
     */
    copy() {
        let items = this._selection.toJSON();
        if (!items || items.length <= 0) return;
        if (!this._isCopySupportedBySelection()) items = [];

        this._adapter.setItems(this._getCurrentContextClassification(), items);
    }

    /**
     * Cut the selected items. This method uses the history and the
     * {@link Ofcs.Editor.Command.CutItemCommand CutItemCommand} for this
     * action.
     */
    cut() {
        const command = new CutItemCommand(this._selection, this._deletableItemsFilter);
        this.copy();
        if (!this._isCopySupportedBySelection()) return;
        this._history.addAndExecuteCommand(command);
    }

    /**
     * Pastes items from clipboard into item collection using the
     * {@link Ofcs.Editor.Command.PasteItemCommand PasteItemCommand}
     */
    paste() {
        const itemData = this._adapter.getItems(this._getCurrentContextClassification());
        const itemCollection = this._contextManager.getCurrentItems();
        const parentItem = this._contextManager.getCurrentItem();

        if (!parentItem.supportsMultiChildCopy && itemData.length > 1) return;

        const itemPromises = itemData.map(data => this._itemFactory.create(data.type, data, parentItem));

        Promise
            .all(itemPromises)
            .then(items => items.filter(item => !!(item && item.supportsCopying)))
            .then((items) => {
                if (items.length === 0) throw new Error('No item can be pasted');
                this._history.addAndExecuteCommand(new PasteItemCommand(itemCollection, items));
                this._selection.clear();
                this._selection.selectItems(items);
                this._centerItems(items);
                return null;
            })
            .catch(e => Logger.warn(e.message));
    }

    isCopyPossible() {
        if (this._selection.length <= 0) return false;
        if (!this._isCopySupportedBySelection()) return false;

        return true;
    }

    /**
     * Position all given items in center of viewport.
     *
     * @param {AbstractItem[]} items
     * @private
     */
    _centerItems(items) {
        const center = this._canvas
            .getCurrentContext()
            .getItem()
            .getBounds()
            .clone()
            .moveToNull()
            .getCenter();

        let bounds;

        items.forEach((item) => {
            if (!bounds) {
                bounds = item.getBounds();
            } else {
                bounds = bounds.merge(item.getBounds());
            }
        });

        const distance = new Distance(
            bounds.getCenter(),
            center,
        );

        items.forEach(item => item.move(distance));
    }

    /**
     * Flushes the clipboard
     */
    clear() {
        this._adapter.clear();
    }

    /**
     * Returns if the clipboard holds items
     *
     * @return {boolean}
     */
    hasItems() {
        return this._adapter.hasItems(this._getCurrentContextClassification());
    }

    /**
     * @return {string}
     * @private
     */
    _getCurrentContextClassification() {
        const currentItem = this._contextManager.getCurrentItem();

        if (!currentItem) return null;

        return currentItem.getClassification();
    }
}
